// Past Winners -- the NIKAO CUP roll of honour.
//
// The champion list lives in src/data/pastWinners.js so adding next year's
// winner is a one-object edit with no JSX changes. Photos are optional: an
// entry without one still renders, just without the portrait.

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Trophy } from 'lucide-react';
import { PAST_WINNERS } from '../data/pastWinners';

export default function PastWinnersTab() {
  if (!PAST_WINNERS.length) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Trophy className="h-5 w-5" />Past Winners</CardTitle>
          <CardDescription>No champions recorded yet.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const [latest, ...earlier] = PAST_WINNERS;

  return (
    <div className="mt-4 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />Past Winners
          </CardTitle>
          <CardDescription>The NIKAO CUP roll of honour.</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
            {latest.photo && (
              <div className="w-full max-w-xs shrink-0">
                <img
                  src={latest.photo}
                  alt={latest.photoAlt || `${latest.name}, ${latest.year} NIKAO Cup champion`}
                  className="mx-auto w-full rounded-lg"
                  loading="lazy"
                />
              </div>
            )}

            <div className="min-w-0 flex-1 text-center sm:pt-4 sm:text-left">
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-900">
                <Trophy className="h-3.5 w-3.5" />
                {latest.year} Champion
              </div>

              <h2 className="mt-3 text-3xl font-bold text-gray-900 sm:text-4xl">
                {latest.name}
              </h2>

              {latest.division && (
                <p className="mt-1 text-sm text-gray-600">{latest.division} Division</p>
              )}

              {latest.inscription && (
                <p className="mt-4 border-l-2 border-amber-300 pl-3 text-sm italic text-gray-600">
                  {latest.inscription}
                </p>
              )}

              {latest.note && (
                <p className="mt-3 text-sm text-gray-700">{latest.note}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {earlier.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Earlier champions</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4 w-20">Year</th>
                  <th className="py-2 pr-4">Champion</th>
                  <th className="py-2">Division</th>
                </tr>
              </thead>
              <tbody>
                {earlier.map(w => (
                  <tr key={w.year} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium tabular-nums text-gray-900">{w.year}</td>
                    <td className="py-2 pr-4 text-gray-900">{w.name}</td>
                    <td className="py-2 text-gray-600">{w.division || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
